import { DungeonEntity } from '../entities/DungeonEntity';
import { mapService } from './MapService';
import { logger } from './LoggerService';
import { animationService } from './AnimationService';
import { PreviewService } from './PreviewService';
import { InventoryService } from './InventoryService';
import { LocationService } from './LocationService';
import { MonsterSpawningService } from './MonsterSpawningService';
import { DungeonEntityType, DirectionEnum } from '../types/enums';
import { PlayerEntity } from '../entities/PlayerEntity';
import { tileWidth, tileHeight } from '../data/tileset.config';
import { DUNGEON_REGISTRY, DEFAULT_DUNGEON_ID } from '../data/dungeons.config';
import { ResolvedFloor } from '../types/floor.types';
import { DungeonRunConfig } from '../types/dungeon.types';
import {
  resolveFloor,
  cloneDungeonRunConfig,
} from '../utils/floor-resolver.utils';
import { PlacedLocation, ITile, IRoom } from '../types/map.types';
import { generateDungeon, generateTestArena } from '../utils/dungeon-generator';
import { emitEntityEvent } from '../utils/buff.utils';
import {
  BOSS_FLOOR_LOCATIONS,
  LOCATION_REGISTRY,
} from '../data/locations.config';
import { useGameStore } from '../store/gameSlice';
import { useSpectatorStore } from '../store/spectatorSlice';
import { useCharacterProgressionStore } from '../store/characterProgressionSlice';
import { useBestiaryStore } from '../store/bestiarySlice';
import {
  MONSTER_CONFIGS,
  DEFAULT_ELITE_SCALING,
} from '../data/monsters.config';
import { useLevelUpFeedStore } from '../store/levelUpFeedSlice';
import { intraRunProgressionService } from './IntraRunProgressionService';
import { runStartProgressionService } from './RunStartProgressionService';
import {
  applyTrinketEffects,
  applyMinorOnAttachedSpellEffects,
  iterateEquippedMinors,
} from './TrinketService';
import { TRINKET_MAJOR_REGISTRY } from '../data/trinkets.config';
import {
  type LoadoutSource,
  type PlayerLoadoutSnapshot,
} from './LoadoutSourceService';
import { SocketService } from './SocketService';
import { useSocketsStore } from '../store/socketsSlice';
import type { SocketSlots } from '../types/modifier.types';
import { MODIFIER_REGISTRY } from '../data/modifiers.config';
import { EFFECT_HANDLERS } from '../effect-system/effectHandlers';
import { useInventoryStore } from '../store/inventorySlice';
import { zoneEffectService } from './ZoneEffectService';
import { cancelPendingRecasts } from '../effect-system/effectHandlers';
import { challengeService } from './ChallengeService';
import { dynamicPlacementService } from './DynamicPlacementService';

type MapService = typeof mapService;
type AnimationService = typeof animationService;

class DungeonService {
  private static instance: DungeonService;
  private entities: DungeonEntity[] = [];
  private entityById = new Map<string, DungeonEntity>();
  private cachedPlayer: PlayerEntity | undefined = undefined;
  private playerCacheDirty = true;
  public localPlayerId: string | null = null;
  public remoteLoadouts = new Map<string, PlayerLoadoutSnapshot>();
  public runPlayerIds = new Set<string>();
  // Client-only: freshest replicated logical cell per entity (`"x,y"` → id), from
  // the latest snapshot. Move prediction reads this instead of the interpolated
  // (≈100 ms-delayed) positionX/Y so it doesn't step into a cell an entity just
  // entered on the host (cf. `freshestOccupantAt`, `DungeonEntity.isEntityBlocking`).
  public clientFreshCells = new Map<string, string>();
  private firstCastSkipsRemaining = 0;
  private runUsedLocationIds = new Set<string>();

  public mapService: MapService;
  public animationService: AnimationService;
  public previewService: PreviewService;
  public inventoryService: InventoryService;
  public locationService: LocationService;
  public monsterSpawningService: MonsterSpawningService;

  public allowPlayerInput = false;
  public restrictedInputType: null | 'dialog' | 'inventory' = null;
  public gameSpeed = 1;
  private needsRender = false;
  private cameraSnapPending = false;

  public markNeedsRender(): void {
    this.needsRender = true;
  }

  /** Request the camera to jump to the player without lerping (e.g. a
   *  full-floor teleport, to avoid sliding across the level). */
  public requestCameraSnap(): void {
    this.cameraSnapPending = true;
  }

  public consumeCameraSnap(): boolean {
    const pending = this.cameraSnapPending;
    this.cameraSnapPending = false;
    return pending;
  }

  private constructor(
    mapService: MapService,
    animationService: AnimationService,
  ) {
    this.mapService = mapService;
    this.animationService = animationService;
    this.mapService.dungeonService = this;
    this.animationService.dungeonService = this;
    this.previewService = new PreviewService();
    this.previewService.dungeonService = this;
    this.inventoryService = new InventoryService();
    this.inventoryService.dungeonService = this;
    this.locationService = new LocationService();
    this.locationService.dungeonService = this;
    this.monsterSpawningService = new MonsterSpawningService();
    this.monsterSpawningService.dungeonService = this;
    intraRunProgressionService.dungeonService = this;
  }

  public static getInstance(): DungeonService {
    if (!DungeonService.instance) {
      DungeonService.instance = new DungeonService(
        mapService,
        animationService,
      );
    }
    return DungeonService.instance;
  }

  public setEntities(entities: DungeonEntity[]): void {
    this.entities = entities;
    this.entityById.clear();
    for (const e of entities) {
      this.entityById.set(e.id, e);
    }
  }

  public getEntities(): DungeonEntity[] {
    return this.entities;
  }

  public getEntityById(id: string): DungeonEntity | undefined {
    return this.entityById.get(id);
  }

  public getPlayers(): PlayerEntity[] {
    return this.entities.filter(
      (entity) => entity.type === DungeonEntityType.PLAYER,
    ) as PlayerEntity[];
  }

  public createPlayerEntity(
    x: number,
    y: number,
    source?: LoadoutSource,
  ): PlayerEntity {
    const runStats = runStartProgressionService.buildRunStats(source);
    const player = new PlayerEntity({
      life: runStats.stats.maxLife,
      maxLife: runStats.stats.maxLife,
      isWalkableOver: false,
      configId: 'player',
      x,
      y,
      stats: runStats.stats,
      loadoutSource: source,
    });
    for (const buff of runStartProgressionService.buildPersistentBuffs()) {
      player.addBuff(buff);
    }
    player.recomputeStats();
    return player;
  }

  /**
   * Spawn a remote (client-controlled) player from a stored loadout snapshot.
   * Its `id` is the controlling peer's `PeerId` so the host can route intents
   * to it and the client can bind via `localPlayerId`. No-op if already
   * spawned or if no snapshot has arrived yet (run-start barrier).
   */
  public spawnRemotePlayer(peerId: string): void {
    if (this.entityById.has(peerId)) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log(
          '[floor-debug] spawnRemotePlayer SKIP already-present',
          peerId,
        );
      }
      return;
    }
    const snapshot = this.remoteLoadouts.get(peerId);
    if (!snapshot) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log(
          '[floor-debug] spawnRemotePlayer SKIP no-loadout',
          peerId,
          'keys=',
          Array.from(this.remoteLoadouts.keys()),
        );
      }
      return;
    }
    const pos = this.monsterSpawningService.findValidSpawnPosition();
    if (!pos) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log(
          '[floor-debug] spawnRemotePlayer SKIP no-pos',
          peerId,
          'players=',
          this.getPlayers().length,
        );
      }
      return;
    }
    const player = this.createPlayerEntity(pos.x, pos.y, snapshot);
    player.id = peerId;
    player.remote = true;
    player.masteryLevels = snapshot.mastery ?? {};
    this.buildPlayerRunEffects(player);
    this.addEntity(player);
    this.runPlayerIds.add(peerId);
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[floor-debug] spawnRemotePlayer OK', peerId, pos);
    }
  }

  public despawnRemotePlayer(peerId: string): void {
    this.removeEntity(peerId);
  }

  public getLocalPlayer(): PlayerEntity | undefined {
    if (this.playerCacheDirty) {
      const players = this.getPlayers();
      this.cachedPlayer = this.localPlayerId
        ? (players.find((p) => p.id === this.localPlayerId) ?? players[0])
        : players[0];
      this.playerCacheDirty = false;
    }
    return this.cachedPlayer;
  }

  // The player this client controls — camera, UI, input bind to it. Until
  // networking sets `localPlayerId`, this is the first (and only) player.
  // `getPlayer()` stays an alias for the single-player call sites.
  public getPlayer(): PlayerEntity | undefined {
    return this.getLocalPlayer();
  }

  public cameraTargetEntity(): DungeonEntity | undefined {
    const spec = useSpectatorStore.getState();
    if (!spec.downedSelf) {
      // Find the local player by id — NOT getLocalPlayer(), whose `?? players[0]`
      // fallback would briefly follow an ally while our entity is absent during
      // a revive. Returning undefined freezes the camera on our last position
      // until we respawn (same cell on a seed-revive → no visible movement).
      return this.localPlayerId
        ? this.getPlayers().find((p) => p.id === this.localPlayerId)
        : this.getLocalPlayer();
    }
    const allies = this.getPlayers().filter((p) => p.id !== this.localPlayerId);
    if (allies.length === 0) {
      return this.getLocalPlayer();
    }
    const watched = allies.find((p) => p.id === spec.watchedAllyId);
    return watched ?? allies[0];
  }

  // Grants XP to the persistent character level. Each level crossed applies its
  // passive stat gain live to the player (delta of the per-level scaled base,
  // matching the run-start bake) and surfaces a level-up snackbar.
  public awardXp(amount: number): void {
    const beforeLevel = useCharacterProgressionStore.getState().level;
    useCharacterProgressionStore.getState().gainXp(amount);
    const afterLevel = useCharacterProgressionStore.getState().level;
    if (afterLevel <= beforeLevel) {
      return;
    }
    const player = this.getPlayer();
    if (!player) {
      return;
    }
    const gained = intraRunProgressionService.applyLevelUps(
      player,
      beforeLevel,
      afterLevel,
    );
    useLevelUpFeedStore.getState().push(afterLevel, gained);
  }

  // The immediate, always-applied progression of a kill (XP + bestiary), run on
  // EVERY browser. The host calls it in MonsterEntity.death(); a client calls it
  // from the replicated 'monster-killed' event (it runs no death()). XP is full
  // per player — computed with the LOCAL character level — matching the private
  // per-player loot model.
  public applyKillProgression(
    configId: string,
    level: number,
    isElite: boolean,
  ): void {
    const baseConfig = MONSTER_CONFIGS[configId];
    if (baseConfig) {
      const eliteMult = isElite ? (DEFAULT_ELITE_SCALING.xpMultiplier ?? 1) : 1;
      // Halved baseline, scaled by the enemy/player level ratio: a target twice
      // the player's level yields 2×, half the level yields 0.5× — curbs
      // early-game over-leveling and rewards fighting above your level.
      const playerLevel = useCharacterProgressionStore.getState().level;
      const levelRatio = playerLevel > 0 ? level / playerLevel : 1;
      const xpGained = Math.max(
        1,
        Math.round(baseConfig.xpBase * level * eliteMult * 0.5 * levelRatio),
      );
      this.awardXp(xpGained);
    }
    useBestiaryStore
      .getState()
      .recordKill(configId, useGameStore.getState().currentFloor);
  }

  public addEntity(entity: DungeonEntity): void {
    this.entities.push(entity);
    this.entityById.set(entity.id, entity);
    this.playerCacheDirty = true;
  }

  public removeEntity(entityId: string): void {
    this.previewService.removeAffectedTilesByEntity(entityId);
    challengeService.onEntityRemoved(entityId);

    this.entities = this.entities.filter((entity) => entity.id !== entityId);
    this.entityById.delete(entityId);
    this.playerCacheDirty = true;
    this.needsRender = true;
  }

  public shouldRender(): boolean {
    return this.needsRender || this.entities.some((entity) => entity.isDirty);
  }

  public clearDirtyFlags(): void {
    this.entities.forEach((entity) => entity.clearDirty());
    this.needsRender = false;
  }

  public getEntityAt(
    x: number,
    y: number,
    excludeId?: string,
  ): DungeonEntity | undefined {
    return this.entities.find(
      (entity) =>
        entity.positionX === x &&
        entity.positionY === y &&
        entity.id !== excludeId,
    );
  }

  /** Client move prediction: id occupying (x,y) per the freshest snapshot, or
   *  undefined. cf. `clientFreshCells`. */
  public freshestOccupantAt(x: number, y: number): string | undefined {
    return this.clientFreshCells.get(`${x},${y}`);
  }

  public reset(): void {
    this.entities = [];
    this.entityById.clear();
    // NB: remoteLoadouts is NOT cleared here. reset() runs inside generateFloor
    // (every floor) and can race the LoadoutGate, which populates remoteLoadouts
    // as soon as a client's loadout arrives — possibly before the (async, bundle-
    // gated) floor generation. Clearing here would wipe a just-received loadout
    // and the remote would never spawn. It's run-scoped (survives floors) and
    // cleared explicitly at host run-start instead (Dungeon host effect).
    this.cachedPlayer = undefined;
    this.playerCacheDirty = true;
    this.localPlayerId = null;
    this.runPlayerIds.clear();
    this.clientFreshCells.clear();
    this.previewService.clearAffectedTiles();
    this.inventoryService.clear();
    this.allowPlayerInput = false;
    this.locationService.clear();
    zoneEffectService.reset();
    challengeService.reset();
    dynamicPlacementService.clear();
    cancelPendingRecasts();
  }

  /** Returns the current floor's monster level (used by spawnMonsters handler). */
  public getCurrentFloorLevel(): number {
    return this.monsterSpawningService.getCurrentFloorLevel();
  }

  public getCurrentBiome(): string | null {
    return this.monsterSpawningService.getCurrentBiome();
  }

  public resetRunMechanics(): void {
    this.firstCastSkipsRemaining = 0;
  }

  public applyPlayerLoadoutEffects(player: PlayerEntity): void {
    const source = player.loadoutSource;
    if (source.major !== null && source.majorLevel !== null) {
      const majorDef = TRINKET_MAJOR_REGISTRY[source.major];
      if (majorDef) {
        applyTrinketEffects(player, source.majorLevel, majorDef);
      }
    }
    for (const { level, def } of iterateEquippedMinors(source)) {
      applyTrinketEffects(player, level, def);
    }
  }

  // Live socket store for the local player; the synced snapshot for a remote.
  private socketsFor(player: PlayerEntity): Record<string, SocketSlots> {
    if (!player.remote) {
      return useSocketsStore.getState().socketed;
    }
    return player.loadoutSource.sockets ?? {};
  }

  // Applies one player's socketed modifiers to ITS OWN spell/buff instances:
  // spellEffects route to the attached spell, globalEffects to the player.
  private applyRunSpellModifiers(
    player: PlayerEntity,
    socketed: Record<string, SocketSlots>,
  ): void {
    for (const { modelId, slot } of iterateEquippedMinors(
      player.loadoutSource,
    )) {
      const modifierIds = socketed[modelId];
      if (!modifierIds) {
        continue;
      }
      for (const modifierId of modifierIds) {
        const def = modifierId ? MODIFIER_REGISTRY[modifierId] : undefined;
        if (!def) {
          continue;
        }
        if (def.spellEffects && slot.spellId) {
          const ctx = {
            caster: player,
            direction: DirectionEnum.TOP,
            affectedCells: [],
            targetSpellId: slot.spellId,
          };
          for (const step of def.spellEffects) {
            EFFECT_HANDLERS[step.handler]?.(ctx, { ...step.params });
          }
        }
        if (def.globalEffects) {
          const ctx = {
            caster: player,
            direction: DirectionEnum.TOP,
            affectedCells: [],
          };
          for (const step of def.globalEffects) {
            EFFECT_HANDLERS[step.handler]?.(ctx, { ...step.params });
          }
        }
      }
    }
  }

  private applyPlayerSpellInstanceModifiers(player: PlayerEntity): void {
    for (const { level, def, slot } of iterateEquippedMinors(
      player.loadoutSource,
    )) {
      if (slot.spellId) {
        applyMinorOnAttachedSpellEffects(player, slot.spellId, level, def);
      }
    }
    this.applyRunSpellModifiers(player, this.socketsFor(player));
  }

  /** (Re)build all run-scoped effects of a SINGLE player from its own loadout:
   *  clears its spell/buff instances, re-applies trinket effects + socket/minor
   *  spell-instance modifiers. Idempotent — safe on mid-run re-sync and resume.
   *  Does not touch other players. */
  public buildPlayerRunEffects(player: PlayerEntity): void {
    player.resetRunInstances();
    this.applyPlayerLoadoutEffects(player);
    this.applyPlayerSpellInstanceModifiers(player);
  }

  public restoreRunUsedLocationIds(ids: string[]): void {
    this.runUsedLocationIds = new Set(ids);
  }

  public getRunUsedLocationIds(): string[] {
    return Array.from(this.runUsedLocationIds);
  }

  public enableFirstCastSkip(amount: number): void {
    this.firstCastSkipsRemaining += amount;
  }

  public consumeFirstCastSkip(): boolean {
    if (this.firstCastSkipsRemaining <= 0) {
      return false;
    }
    this.firstCastSkipsRemaining -= 1;
    return true;
  }

  public initialize(floorNumber = 1): void {
    this.allowPlayerInput = true;
    void floorNumber;
  }

  /**
   * Returns the run-scoped dungeon config, seeding it from the registry if it
   * is missing or stale (e.g. a fresh run, or a resumed save without one).
   */
  public ensureRunConfig(): DungeonRunConfig {
    const store = useGameStore.getState();
    const id = store.currentDungeonId ?? DEFAULT_DUNGEON_ID;
    const existing = store.dungeonRunConfig;
    if (existing && existing.dungeonId === id) {
      return existing;
    }
    const def = DUNGEON_REGISTRY[id] ?? DUNGEON_REGISTRY[DEFAULT_DUNGEON_ID];
    const cloned = cloneDungeonRunConfig(def.id, def.initialConfig);
    store.setDungeonRunConfig(cloned);
    return cloned;
  }

  /** Resolves the effective config of a floor from the run config. */
  public resolveFloorInfo(floor: number): ResolvedFloor {
    return resolveFloor(this.ensureRunConfig(), floor);
  }

  public getCurrentDungeonFloorCount(): number {
    return this.ensureRunConfig().floorCount;
  }

  /** Get all entities (used by voidCry effect handler). */
  public getAllEntities(): DungeonEntity[] {
    return this.entities;
  }

  /** Generic game event bus — set by Dungeon.tsx. To interact with React layer. */
  public onGameEvent: (type: string, payload?: unknown) => void = () => {};

  public emitGameEvent(type: string, payload?: unknown): void {
    this.onGameEvent(type, payload);
  }

  /** Set the current floor config (for spawner biome weights). */
  public setFloorConfig(floorConfig: ResolvedFloor): void {
    this.monsterSpawningService.setFloorConfig(floorConfig);
  }

  public generateFloor(
    floorNumber: number,
    existingPlayer?: PlayerEntity,
  ): {
    tiles: ITile[];
    width: number;
    height: number;
    spawnX: number;
    spawnY: number;
    floorConfig: ResolvedFloor;
    rooms: IRoom[];
    placedLocations: PlacedLocation[];
  } {
    // Run-scoped spell/buff instances persist across floors (the player is reused).
    // On a fresh run buildPlayerRunEffects (called below) owns the reset.
    if (!existingPlayer) {
      this.runUsedLocationIds.clear();
    }
    this.resetRunMechanics();

    // A floor change cancels anything still in flight: the carried-over player's
    // current cast, scheduled spell recasts, and every spell preview tile left
    // on the previous floor.
    existingPlayer?.cancelCast();
    cancelPendingRecasts();
    this.previewService.clearAffectedTiles();

    const floorConfig = resolveFloor(this.ensureRunConfig(), floorNumber);
    const isBoss = floorConfig.floorType === 'boss';
    const baseLocations = isBoss
      ? BOSS_FLOOR_LOCATIONS
      : floorConfig.biome.possibleLocations;
    const locations = baseLocations.filter((loc) => {
      if (
        loc.floorRange &&
        (floorNumber < loc.floorRange[0] || floorNumber > loc.floorRange[1])
      ) {
        return false;
      }
      if (
        loc.minMonsterLevel !== undefined &&
        floorConfig.averageMonsterLevel < loc.minMonsterLevel
      ) {
        return false;
      }
      if (loc.oncePerRun && this.runUsedLocationIds.has(loc.id)) {
        return false;
      }
      return true;
    });
    const roomTemplates = isBoss
      ? []
      : (floorConfig.biome.roomTemplates ?? []).filter((tpl) => {
          for (const tl of tpl.locations) {
            const ldef = LOCATION_REGISTRY[tl.locationId];
            if (!ldef) {
              continue;
            }
            if (
              ldef.floorRange &&
              (floorNumber < ldef.floorRange[0] ||
                floorNumber > ldef.floorRange[1])
            ) {
              return false;
            }
            if (ldef.oncePerRun && this.runUsedLocationIds.has(ldef.id)) {
              return false;
            }
          }
          return true;
        });

    const isTestArena = floorConfig.biome.generation === 'test-arena';
    const { tiles, width, height, rooms, placedLocations, debug } = isTestArena
      ? generateTestArena(floorConfig.width, floorConfig.height)
      : generateDungeon({
          width: floorConfig.width,
          height: floorConfig.height,
          style: floorConfig.style,
          locations,
          roomTemplates,
          roomCount: floorConfig.roomCount,
        });

    this.mapService.setGenerationDebug(debug ?? null);
    if (debug) {
      logger.info(
        `Floor ${floorNumber} gen [${debug.style}] ${debug.width}×${debug.height}: target ${debug.targetCount}, placed ${debug.placedCount}, dropped ${debug.droppedCount}, rooms ${debug.rooms.length}, corridors ${debug.corridorCount}`,
      );
      for (const r of debug.rooms) {
        logger.log(
          `  room ${r.index} ${r.family}${r.templateId ? ` (${r.templateId})` : ''} bbox (${r.bbox.x},${r.bbox.y}) ${r.bbox.width}×${r.bbox.height} cells ${r.cellCount} center (${r.center.x},${r.center.y})`,
        );
      }
    }

    logger.info(
      `Floor ${floorNumber} locations (${placedLocations.length}):`,
      placedLocations.map((p) => `${p.id}@(${p.x},${p.y})`).join(', '),
    );

    for (const placed of placedLocations) {
      const def = LOCATION_REGISTRY[placed.id];
      if (def?.oncePerRun) {
        this.runUsedLocationIds.add(placed.id);
      }
    }

    this.reset();
    this.mapService.clearDiscoveredTiles();

    const spawnLocation = placedLocations.find((p) => p.id === 'spawn-point');
    const fallbackCell =
      rooms[0].cells.length > 0
        ? rooms[0].cells[Math.floor(rooms[0].cells.length / 2)]
        : {
            x: Math.floor(rooms[0].x + rooms[0].width / 2),
            y: Math.floor(rooms[0].y + rooms[0].height / 2),
          };
    const spawnX = spawnLocation ? spawnLocation.x : fallbackCell.x;
    const spawnY = spawnLocation ? spawnLocation.y : fallbackCell.y;

    if (existingPlayer) {
      existingPlayer.positionX = spawnX;
      existingPlayer.positionY = spawnY;
      existingPlayer.absoluteX = spawnX * tileWidth;
      existingPlayer.absoluteY = spawnY * tileHeight;
      existingPlayer.markDirty();
      this.addEntity(existingPlayer);
      this.localPlayerId = existingPlayer.id;
      // reset() above cleared runPlayerIds; re-add the carried player so co-op
      // TPK accounting counts it on floor 2+ (remotes are re-added by the host
      // peer sweep). Without this the host is absent from runPlayerIds past
      // floor 1, so a lone remote's death reads as a full wipe.
      this.runPlayerIds.add(existingPlayer.id);
    } else {
      // Fresh run start (floor 1, no carried-over player): wipe any modifiers
      // left over from a previous run that was quit-and-saved but not resumed.
      // Within a run, sockets persist across floors (only the `existingPlayer`
      // branch is taken on descent).
      SocketService.clearRunModifiers();
      const player = this.createPlayerEntity(spawnX, spawnY);
      this.buildPlayerRunEffects(player);
      this.addEntity(player);
      this.localPlayerId = player.id;
      this.runPlayerIds.add(player.id);
    }

    const activePlayer = this.getPlayer();
    if (activePlayer) {
      // Force a recompute before reading derived stats: without it,
      // `inventorySlots` still reflects baseStats (persistent buffs such as
      // Voyageur not yet applied → resize is a no-op).
      activePlayer.recomputeStats();
      useInventoryStore.getState().resize(activePlayer.stats.inventorySlots);
      emitEntityEvent(activePlayer, 'onFloorEnter');
    }

    // Register locations before populating monsters: the initial fill rejects
    // location/POI cells via `isSpawnableCell` → `isLocationAt`, which reads
    // this list. Set it after the spawn and mobs could roll onto the stairs.
    this.locationService.setPlacedLocations(placedLocations);

    if (isTestArena) {
      this.monsterSpawningService.populateTestArena(rooms[0]);
    } else {
      this.monsterSpawningService.populateFloor(
        tiles,
        floorConfig,
        rooms,
        placedLocations,
      );
    }

    this.initialize(floorNumber);
    this.setFloorConfig(floorConfig);
    this.monsterSpawningService.setSpawnerEnabled(
      floorConfig.floorType !== 'boss' && !isTestArena,
    );
    useGameStore.getState().setCurrentFloor(floorNumber);

    return {
      tiles,
      width,
      height,
      spawnX,
      spawnY,
      floorConfig,
      rooms,
      placedLocations,
    };
  }
}

export const dungeonService = DungeonService.getInstance();
